import { createCheckbox } from "@/client/ui/design-system/components/checkbox.component";
import { createRadioButton } from "@/client/ui/design-system/components/radio-button.component";
import { createRangeSliderComponent } from "@/client/ui/design-system/components/range-slider.component";
import { createSliderComponent } from "@/client/ui/design-system/components/slider.component";
import { createSvgIcon, type IconName } from "@/client/ui/design-system/icons";
import { Theme } from "@/client/ui/design-system/styles/colors.css";
import { createEl } from "@/client/utils/createEl";
import { SpliceAssetCategory, SpliceSortType } from "@/shared/splice-search.api";
import { hasActiveFilters } from "./sounds-browser-actions";
import type { SoundsBrowserContext, SoundsBrowserFilters } from "./sounds-browser.api";
import { px } from "@/client/utils/units";
import * as styles from "./sounds-browser.css";

/**
 * The filter row as the Sounds plugin has it: each filter is a small pill
 * that opens a popover menu. A set filter turns the pill primary and grows an
 * x that clears it. Type applies as soon as it is picked; Key and the tag
 * menus apply on every click and close on "Close"; BPM applies on "Save",
 * because a range is two numbers and half a range is not a filter yet.
 *
 * Under the row, the tags the result set shares are chips to toggle on and
 * off. Sort lives in the list header, as it does in the plugin.
 */
export interface SoundsBrowserFiltersComponent {
    readonly filterRow: HTMLElement;
    readonly tagRow: HTMLElement;
    readonly sortControls: HTMLElement;
}

const kInstruments = [
    "drums",
    "bass",
    "vocals",
    "piano",
    "keys",
    "guitar",
    "synth",
    "pad",
    "strings",
    "brass",
    "percussion",
    "fx",
];

const kGenres = [
    "hip hop",
    "trap",
    "pop",
    "r&b",
    "house",
    "techno",
    "electronic",
    "ambient",
    "lo-fi",
    "drill",
    "afrobeats",
    "latin",
    "jazz",
    "rock",
];

const kBpmMin = 1;
const kBpmMax = 300;

/** Sort values travel as plain strings in the filters; this is the shuffle one. */
const kRandomSort: string = SpliceSortType.Random;

/** The catalog names keys with sharps; the flat view is the same twelve keys spelt the other way. */
const kNaturals = ["c", "d", "e", "f", "g", "a", "b"] as const;
const kSharpGroups: readonly (readonly string[])[] = [
    ["c#", "d#"],
    ["f#", "g#", "a#"],
];
const kFlatNames: Readonly<Record<string, string>> = {
    "c#": "D♭",
    "d#": "E♭",
    "f#": "G♭",
    "g#": "A♭",
    "a#": "B♭",
};

const formatKeyLabel = (key: string, chordType: string, viewFlats: boolean): string => {
    if (key === "") {
        return chordType === "" ? "Key" : capitalize(chordType);
    }
    const root =
        key.endsWith("#") && viewFlats
            ? kFlatNames[key]
            : `${key.charAt(0).toUpperCase()}${key.endsWith("#") ? "♯" : ""}`;
    return chordType === "" ? root : `${root} ${capitalize(chordType)}`;
};

const capitalize = (value: string): string =>
    value.length === 0 ? value : `${value.charAt(0).toUpperCase()}${value.slice(1)}`;

const formatBpmLabel = (min: number | undefined, max: number | undefined): string => {
    if (min !== undefined && max !== undefined) {
        return min === max ? `${min} BPM` : `${min} - ${max} BPM`;
    }
    if (min !== undefined) {
        return `${min}+ BPM`;
    }
    if (max !== undefined) {
        return `Up to ${max} BPM`;
    }
    return "BPM";
};

const parseBpm = (raw: string): number | undefined => {
    const trimmed = raw.trim();
    if (trimmed === "") {
        return undefined;
    }
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) && parsed > 0
        ? Math.min(kBpmMax, Math.max(kBpmMin, Math.round(parsed)))
        : undefined;
};

// ── Menu shell ───────────────────────────────────────────────────────────────

interface FilterMenuOptions {
    /** What the pill reads. Re-read whenever the filters change. */
    readonly getLabel: () => string;
    readonly getIsActive: () => boolean;
    readonly onClear: () => void;
    readonly content: HTMLElement;
    /** Adds the Clear / Save footer. Without it the menu is choose-and-close. */
    readonly actions?: { readonly saveLabel: string; readonly onSave: () => void };
    /** Menu content full width, for tabs and grids that own their own padding. */
    readonly isFlush?: boolean;
    readonly ariaLabel: string;
    /** An icon-only pill, as sort's is. */
    readonly icon?: IconName;
}

interface FilterMenu {
    readonly el: HTMLElement;
    readonly refresh: () => void;
    readonly close: () => void;
    readonly onOpen: (listener: () => void) => void;
}

/** Only one menu open at a time, as with the plugin's overlays. */
let closeOpenMenu: (() => void) | undefined;

const createFilterMenu = (options: FilterMenuOptions): FilterMenu => {
    let isOpen = false;
    const openListeners: (() => void)[] = [];

    const labelEl = createEl("span", "", options.getLabel());
    const chevron = createSvgIcon("chevronDown", styles.filterButtonIcon);
    const clearEl = createEl(
        "span",
        styles.filterButtonClear,
        [createSvgIcon("x", styles.filterButtonIcon)],
        (el) => {
            el.setAttribute("role", "button");
            el.setAttribute("aria-label", `Clear ${options.ariaLabel}`);
            el.addEventListener("click", (event) => {
                event.stopPropagation();
                close();
                options.onClear();
            });
        },
    );

    const button = createEl(
        "button",
        styles.filterButton,
        options.icon !== undefined
            ? [createSvgIcon(options.icon, styles.filterButtonIcon), labelEl, chevron, clearEl]
            : [labelEl, chevron, clearEl],
        (el) => {
            el.type = "button";
            el.setAttribute("aria-haspopup", "dialog");
            el.setAttribute("aria-expanded", "false");
            el.setAttribute("aria-label", options.ariaLabel);
            el.addEventListener("click", () => {
                if (isOpen) {
                    close();
                } else {
                    open();
                }
            });
        },
    );

    const footer =
        options.actions !== undefined
            ? createEl("div", styles.filterActions, [
                  createEl("button", styles.filterLinkButton, "Clear", (el) => {
                      el.type = "button";
                      el.addEventListener("click", () => {
                          options.onClear();
                          refresh();
                      });
                  }),
                  createEl(
                      "button",
                      `${styles.filterButton} ${styles.filterButtonActive}`,
                      options.actions.saveLabel,
                      (el) => {
                          el.type = "button";
                          el.addEventListener("click", () => {
                              options.actions?.onSave();
                              close();
                          });
                      },
                  ),
              ])
            : undefined;

    const popover = createEl(
        "div",
        styles.filterPopover,
        [
            createEl(
                "div",
                options.isFlush === true
                    ? `${styles.filterContent} ${styles.filterContentFlush}`
                    : styles.filterContent,
                [options.content],
            ),
            ...(footer !== undefined ? [footer] : []),
        ],
        (el) => {
            el.setAttribute("role", "dialog");
            el.setAttribute("aria-label", options.ariaLabel);
        },
    );

    const el = createEl("div", styles.filterMenu, [button, popover]);

    const open = (): void => {
        closeOpenMenu?.();
        isOpen = true;
        closeOpenMenu = close;
        // Under the pill's left edge when there is room, otherwise slid left
        // just enough to stay inside the panel's gutter: pinning to the pill's
        // right edge instead pushed a menu off the window's left edge once the
        // pills to its left had grown their active labels.
        const hostRect = el.closest("aside")?.getBoundingClientRect();
        const hostLeft = hostRect?.left ?? 0;
        const hostRight = hostRect?.right ?? window.innerWidth;
        const pillLeft = el.getBoundingClientRect().left;
        const width = popover.offsetWidth;
        const minLeft = hostLeft + styles.kPanelInset_px;
        const maxLeft = Math.max(minLeft, hostRight - styles.kPanelInset_px - width);
        const left = Math.min(Math.max(pillLeft, minLeft), maxLeft);
        popover.style.left = px(left - pillLeft);
        popover.classList.add(styles.filterPopoverOpen);
        button.setAttribute("aria-expanded", "true");
        for (const listener of openListeners) {
            listener();
        }
    };

    const close = (): void => {
        if (!isOpen) {
            return;
        }
        isOpen = false;
        if (closeOpenMenu === close) {
            closeOpenMenu = undefined;
        }
        popover.classList.remove(styles.filterPopoverOpen);
        button.setAttribute("aria-expanded", "false");
    };

    const refresh = (): void => {
        const isActive = options.getIsActive();
        labelEl.textContent = options.getLabel();
        button.classList.toggle(styles.filterButtonActive, isActive);
        clearEl.style.display = isActive ? "" : "none";
        chevron.style.display = isActive ? "none" : "";
    };
    refresh();

    document.addEventListener("click", (event) => {
        if (isOpen && !el.contains(event.target as Node)) {
            close();
        }
    });
    el.addEventListener("keydown", (event) => {
        if (event.key === "Escape" && isOpen) {
            event.stopPropagation();
            close();
            button.focus();
        }
    });

    return { el, refresh, close, onOpen: (listener) => openListeners.push(listener) };
};

const createTabs = (
    tabs: readonly { readonly id: string; readonly label: string }[],
    initial: string,
    onChange: (id: string) => void,
): { el: HTMLElement; setActive: (id: string) => void } => {
    const buttons = new Map<string, HTMLButtonElement>();
    const setActive = (id: string): void => {
        for (const [tabId, button] of buttons) {
            button.classList.toggle(styles.filterTabActive, tabId === id);
            button.setAttribute("aria-selected", String(tabId === id));
        }
    };
    const el = createEl(
        "div",
        styles.filterTabs,
        tabs.map((tab) =>
            createEl("button", styles.filterTab, tab.label, (button) => {
                button.type = "button";
                button.setAttribute("role", "tab");
                buttons.set(tab.id, button);
                button.addEventListener("click", () => {
                    setActive(tab.id);
                    onChange(tab.id);
                });
            }),
        ),
        (root) => root.setAttribute("role", "tablist"),
    );
    setActive(initial);
    return { el, setActive };
};

// ── The filters ──────────────────────────────────────────────────────────────

export const createSoundsBrowserFilters = (
    context: SoundsBrowserContext,
): SoundsBrowserFiltersComponent => {
    const { state, signals, actions } = context;
    const menus: FilterMenu[] = [];
    const filters = (): SoundsBrowserFilters => state.filters;

    // ── Type ─────────────────────────────────────────────────────────────────
    const typeOptions: readonly { value: SpliceAssetCategory; label: string }[] = [
        { value: SpliceAssetCategory.Loop, label: "Loops" },
        { value: SpliceAssetCategory.OneShot, label: "One-shots" },
    ];
    const typeRadios = new Map<SpliceAssetCategory, HTMLInputElement>();
    const typeContent = createEl(
        "div",
        "",
        typeOptions.map((option) =>
            createEl("label", styles.choiceRow, [
                createRadioButton(
                    {
                        theme: Theme.Dark,
                        name: "sounds-browser-type",
                        value: option.value,
                        onChange: () => {
                            actions.setCategory(option.value);
                            typeMenu.close();
                        },
                    },
                    (el) => typeRadios.set(option.value, el),
                ),
                createEl("span", "", option.label),
            ]),
        ),
    );
    const typeMenu = createFilterMenu({
        ariaLabel: "Sample type",
        getLabel: () =>
            typeOptions.find((option) => option.value === filters().category)?.label ?? "Type",
        getIsActive: () => filters().category !== SpliceAssetCategory.All,
        onClear: () => actions.setCategory(SpliceAssetCategory.All),
        content: typeContent,
    });
    menus.push(typeMenu);

    // ── Key ──────────────────────────────────────────────────────────────────
    let viewFlats = false;
    const keyCells = new Map<string, HTMLButtonElement>();
    const chordCells = new Map<string, HTMLButtonElement>();

    const createKeyCell = (key: string, label: string, isWide = false): HTMLButtonElement =>
        createEl(
            "button",
            isWide ? `${styles.keyCell} ${styles.keyCellWide}` : styles.keyCell,
            label,
            (el) => {
                el.type = "button";
                el.setAttribute("aria-label", `Key ${label}`);
                el.addEventListener("click", () => {
                    const next = filters().key === key ? "" : key;
                    actions.setKey(next, filters().chordType);
                });
            },
        );

    const accidentalsEl = createEl("div", styles.keyAccidentals, []);
    const renderAccidentals = (): void => {
        for (const key of [...keyCells.keys()]) {
            if (key.endsWith("#")) {
                keyCells.delete(key);
            }
        }
        accidentalsEl.replaceChildren(
            ...kSharpGroups.map((group) =>
                createEl(
                    "div",
                    styles.keySubgroup,
                    group.map((key) => {
                        const label = viewFlats
                            ? kFlatNames[key]
                            : `${key.charAt(0).toUpperCase()}♯`;
                        const cell = createKeyCell(key, label);
                        keyCells.set(key, cell);
                        return cell;
                    }),
                ),
            ),
        );
        refreshKey();
    };

    const naturalsEl = createEl(
        "div",
        styles.keyGroup,
        kNaturals.map((key) => {
            const cell = createKeyCell(key, key.toUpperCase());
            keyCells.set(key, cell);
            return cell;
        }),
    );

    const chordEl = createEl(
        "div",
        styles.chordGroup,
        ["major", "minor"].map((chordType) =>
            createEl(
                "button",
                `${styles.keyCell} ${styles.keyCellWide}`,
                capitalize(chordType),
                (el) => {
                    el.type = "button";
                    chordCells.set(chordType, el);
                    el.addEventListener("click", () => {
                        const next = filters().chordType === chordType ? "" : chordType;
                        actions.setKey(filters().key, next);
                    });
                },
            ),
        ),
    );

    const keyTabs = createTabs(
        [
            { id: "flat", label: "Flat keys" },
            { id: "sharp", label: "Sharp keys" },
        ],
        "sharp",
        (id) => {
            viewFlats = id === "flat";
            renderAccidentals();
            keyMenu.refresh();
        },
    );

    const refreshKey = (): void => {
        keyCells.forEach((cell, key) => {
            cell.classList.toggle(styles.keyCellSelected, filters().key === key);
        });
        chordCells.forEach((cell, chordType) => {
            cell.classList.toggle(styles.keyCellSelected, filters().chordType === chordType);
        });
    };

    const keyContent = createEl("div", "", [
        keyTabs.el,
        createEl("div", styles.keyForm, [accidentalsEl, naturalsEl, chordEl]),
    ]);
    const keyMenu = createFilterMenu({
        ariaLabel: "Key",
        getLabel: () => formatKeyLabel(filters().key, filters().chordType, viewFlats),
        getIsActive: () => filters().key !== "" || filters().chordType !== "",
        onClear: () => actions.setKey("", ""),
        content: keyContent,
        actions: { saveLabel: "Close", onSave: () => undefined },
        isFlush: true,
    });
    menus.push(keyMenu);
    renderAccidentals();

    // ── BPM ──────────────────────────────────────────────────────────────────
    // Edited here, applied on Save: a range is two numbers and half a range
    // is not a filter yet.
    let draftMin: number | undefined;
    let draftMax: number | undefined;
    let draftExact: number | undefined;
    let isExactView = false;

    const minInput = createEl("input", styles.bpmNumberInput, [], (el) => {
        el.type = "number";
        el.inputMode = "numeric";
        el.placeholder = "Min";
        el.min = String(kBpmMin);
        el.max = String(kBpmMax);
        el.setAttribute("aria-label", "Minimum BPM");
    });
    const maxInput = createEl("input", styles.bpmNumberInput, [], (el) => {
        el.type = "number";
        el.inputMode = "numeric";
        el.placeholder = "Max";
        el.min = String(kBpmMin);
        el.max = String(kBpmMax);
        el.setAttribute("aria-label", "Maximum BPM");
    });
    const exactInput = createEl("input", styles.bpmNumberInput, [], (el) => {
        el.type = "number";
        el.inputMode = "numeric";
        el.placeholder = "BPM";
        el.min = String(kBpmMin);
        el.max = String(kBpmMax);
        el.setAttribute("aria-label", "Exact BPM");
    });
    const rangeSlider = createRangeSliderComponent({
        theme: Theme.Dark,
        min: kBpmMin,
        max: kBpmMax,
        step: 1,
        ariaLabelLow: "Minimum BPM",
        ariaLabelHigh: "Maximum BPM",
        onInput: (low, high) => {
            draftMin = low;
            draftMax = high;
            minInput.value = String(low);
            maxInput.value = String(high);
        },
    });
    const exactSlider = createSliderComponent({
        theme: Theme.Dark,
        min: kBpmMin,
        max: kBpmMax,
        step: 1,
        ariaLabel: "Exact BPM",
        onInput: (value) => {
            draftExact = value;
            exactInput.value = String(value);
        },
    });
    const errorEl = createEl("div", styles.bpmError, "Max must be greater than min.", (el) => {
        el.style.display = "none";
    });

    const syncBpmControls = (): void => {
        minInput.value = draftMin !== undefined ? String(draftMin) : "";
        maxInput.value = draftMax !== undefined ? String(draftMax) : "";
        rangeSlider.setValues(draftMin ?? kBpmMin, draftMax ?? kBpmMax);
        exactInput.value = draftExact !== undefined ? String(draftExact) : "";
        exactSlider.value = String(draftExact ?? kBpmMin);
        errorEl.style.display =
            draftMin !== undefined && draftMax !== undefined && draftMax < draftMin ? "" : "none";
    };
    minInput.addEventListener("input", () => {
        draftMin = parseBpm(minInput.value);
        rangeSlider.setValues(draftMin ?? kBpmMin, draftMax ?? kBpmMax);
        errorEl.style.display =
            draftMin !== undefined && draftMax !== undefined && draftMax < draftMin ? "" : "none";
    });
    maxInput.addEventListener("input", () => {
        draftMax = parseBpm(maxInput.value);
        rangeSlider.setValues(draftMin ?? kBpmMin, draftMax ?? kBpmMax);
        errorEl.style.display =
            draftMin !== undefined && draftMax !== undefined && draftMax < draftMin ? "" : "none";
    });
    exactInput.addEventListener("input", () => {
        draftExact = parseBpm(exactInput.value);
        exactSlider.value = String(draftExact ?? kBpmMin);
    });

    const saveBpm = (): void => {
        if (isExactView) {
            actions.setBpmRange(draftExact, draftExact);
            return;
        }
        if (draftMin !== undefined && draftMax !== undefined && draftMax < draftMin) {
            return;
        }
        actions.setBpmRange(draftMin, draftMax);
    };
    for (const input of [minInput, maxInput, exactInput]) {
        input.addEventListener("keydown", (event) => {
            if (event.key === "Enter") {
                saveBpm();
                bpmMenu.close();
            }
        });
    }

    const rangeView = createEl("div", styles.bpmForm, [
        createEl("div", styles.bpmInputsRow, [
            createEl("span", styles.bpmInputsLabel, "Range"),
            createEl("div", styles.bpmInputs, [
                minInput,
                createEl("span", styles.bpmDash, "-"),
                maxInput,
            ]),
        ]),
        rangeSlider.el,
        createEl("div", styles.bpmSliderLabels, [
            createEl("span", "", String(kBpmMin)),
            createEl("span", "", String(kBpmMax)),
        ]),
        errorEl,
    ]);
    const exactView = createEl("div", styles.bpmForm, [
        createEl("div", styles.bpmInputsRow, [
            createEl("span", styles.bpmInputsLabel, "Exact"),
            exactInput,
        ]),
        exactSlider,
        createEl("div", styles.bpmSliderLabels, [
            createEl("span", "", String(kBpmMin)),
            createEl("span", "", String(kBpmMax)),
        ]),
    ]);
    const bpmTabs = createTabs(
        [
            { id: "range", label: "Range" },
            { id: "exact", label: "Exact" },
        ],
        "range",
        (id) => {
            isExactView = id === "exact";
            rangeView.style.display = isExactView ? "none" : "";
            exactView.style.display = isExactView ? "" : "none";
        },
    );
    exactView.style.display = "none";

    const bpmMenu = createFilterMenu({
        ariaLabel: "BPM",
        getLabel: () => formatBpmLabel(filters().bpmMin, filters().bpmMax),
        getIsActive: () => filters().bpmMin !== undefined || filters().bpmMax !== undefined,
        onClear: () => {
            draftMin = draftMax = draftExact = undefined;
            actions.setBpmRange(undefined, undefined);
            syncBpmControls();
        },
        content: createEl("div", "", [bpmTabs.el, rangeView, exactView]),
        actions: { saveLabel: "Save", onSave: saveBpm },
        isFlush: true,
    });
    // Opening shows the applied values, not whatever a cancelled edit left.
    bpmMenu.onOpen(() => {
        const { bpmMin, bpmMax } = filters();
        const isExact = bpmMin !== undefined && bpmMin === bpmMax;
        isExactView = isExact;
        bpmTabs.setActive(isExact ? "exact" : "range");
        rangeView.style.display = isExact ? "none" : "";
        exactView.style.display = isExact ? "" : "none";
        draftMin = isExact ? undefined : bpmMin;
        draftMax = isExact ? undefined : bpmMax;
        draftExact = isExact ? bpmMin : undefined;
        syncBpmControls();
    });
    menus.push(bpmMenu);

    // ── Instruments and Genres ───────────────────────────────────────────────
    // Multi-select from a list, as the plugin's tag filters are. The catalog's
    // own tags for the current results join the list, so a term the user could
    // not have guessed is one tap away.
    const createTagsMenu = (
        label: string,
        base: readonly string[],
        getSelected: () => readonly string[],
        setSelected: (tags: readonly string[]) => void,
    ): FilterMenu => {
        const listEl = createEl("div", "", []);
        let extra: readonly string[] = [];
        const render = (): void => {
            const selected = getSelected();
            const all = Array.from(new Set([...base, ...extra]));
            listEl.replaceChildren(
                ...all.map((tag) =>
                    createEl("label", styles.choiceRow, [
                        createCheckbox({
                            theme: Theme.Dark,
                            checked: selected.includes(tag),
                            onChange: (isChecked) => {
                                setSelected(
                                    isChecked
                                        ? [...getSelected(), tag]
                                        : getSelected().filter((t) => t !== tag),
                                );
                            },
                        }),
                        createEl("span", "", tag),
                    ]),
                ),
            );
        };
        signals.tagSummary.listen((tags) => {
            extra = tags;
            render();
        });
        signals.filters.listen(render);
        render();
        const menu = createFilterMenu({
            ariaLabel: label,
            getLabel: () => {
                const count = getSelected().length;
                return count > 1 ? `${label} · ${count}` : count === 1 ? getSelected()[0] : label;
            },
            getIsActive: () => getSelected().length > 0,
            onClear: () => setSelected([]),
            content: listEl,
            actions: { saveLabel: "Close", onSave: () => undefined },
        });
        return menu;
    };
    const instrumentsMenu = createTagsMenu(
        "Instruments",
        kInstruments,
        () => filters().instruments,
        actions.setInstruments,
    );
    const genresMenu = createTagsMenu("Genres", kGenres, () => filters().genres, actions.setGenres);
    menus.push(instrumentsMenu, genresMenu);

    // ── Clear all ────────────────────────────────────────────────────────────
    const clearAllButton = createEl(
        "button",
        `${styles.filterLinkButton} ${styles.filterClearAll}`,
        [document.createTextNode("Clear all filters"), createSvgIcon("x", styles.filterButtonIcon)],
        (el) => {
            el.type = "button";
            el.style.display = "none";
            el.style.marginLeft = "4px";
            el.addEventListener("click", actions.clearFilters);
        },
    );

    const filterRow = createEl("div", styles.filterRow, [
        typeMenu.el,
        keyMenu.el,
        bpmMenu.el,
        instrumentsMenu.el,
        genresMenu.el,
        clearAllButton,
    ]);

    // ── Tag row ──────────────────────────────────────────────────────────────
    const tagRow = createEl("div", styles.tagRow, [], (el) => {
        el.style.display = "none";
    });
    let summaryTags: readonly string[] = [];
    const renderTagRow = (): void => {
        const selected = filters().tags;
        // Selected tags stay in the row even when a later result set no
        // longer lists them, so they can be turned off where they were turned on.
        const shown = Array.from(new Set([...selected, ...summaryTags])).slice(0, 12);
        tagRow.replaceChildren(
            ...shown.map((tag) =>
                createEl(
                    "button",
                    selected.includes(tag)
                        ? `${styles.tagChip} ${styles.tagChipSelected}`
                        : styles.tagChip,
                    tag,
                    (el) => {
                        el.type = "button";
                        el.setAttribute("aria-pressed", String(selected.includes(tag)));
                        el.addEventListener("click", () => actions.toggleTag(tag));
                    },
                ),
            ),
        );
        tagRow.style.display = shown.length > 0 ? "" : "none";
    };
    signals.tagSummary.listen((tags) => {
        summaryTags = tags;
        renderTagRow();
    });

    // ── Sort, for the list header ────────────────────────────────────────────
    // The empty value is each scope's own default: the catalog opens on
    // relevance, the library on what was licensed most recently.
    const kCatalogSorts: readonly { value: string; label: string }[] = [
        { value: "", label: "Relevance" },
        { value: SpliceSortType.Popularity, label: "Popular" },
        { value: SpliceSortType.Recency, label: "Newest" },
    ];
    const kLibrarySorts: readonly { value: string; label: string }[] = [
        { value: "", label: "Most recent" },
        { value: SpliceSortType.Relevance, label: "Relevance" },
        { value: SpliceSortType.Popularity, label: "Popular" },
    ];
    const sortOptions = (): readonly { value: string; label: string }[] =>
        filters().isLibraryOnly ? kLibrarySorts : kCatalogSorts;
    const sortList = createEl("div", "", []);
    const renderSort = (): void => {
        sortList.replaceChildren(
            createEl("div", styles.sortMenuTitle, "Sort by"),
            ...sortOptions().map((option) => {
                const isSelected = filters().sort === option.value;
                return createEl(
                    "button",
                    isSelected
                        ? `${styles.sortMenuItem} ${styles.sortMenuItemSelected}`
                        : styles.sortMenuItem,
                    [
                        createEl("span", "", option.label),
                        ...(isSelected ? [createSvgIcon("check", styles.sortMenuCheck)] : []),
                    ],
                    (el) => {
                        el.type = "button";
                        el.setAttribute("role", "menuitemradio");
                        el.setAttribute("aria-checked", String(isSelected));
                        el.addEventListener("click", () => {
                            actions.setSort(option.value);
                            sortMenu.close();
                        });
                    },
                );
            }),
        );
    };
    const sortMenu = createFilterMenu({
        ariaLabel: "Sort",
        icon: "select",
        getLabel: () =>
            filters().sort === kRandomSort
                ? "Shuffled"
                : (sortOptions().find((option) => option.value === filters().sort)?.label ??
                  sortOptions()[0].label),
        // Sort is never "active" in the clearable sense: there is always an order.
        getIsActive: () => false,
        onClear: () => actions.setSort(""),
        content: sortList,
    });
    renderSort();
    const shuffleButton = createEl(
        "button",
        `${styles.filterButton} ${styles.shuffleButton}`,
        [createSvgIcon("shuffle", styles.filterButtonIcon)],
        (el) => {
            el.type = "button";
            el.title = "Shuffle";
            el.setAttribute("aria-label", "Shuffle results");
            el.addEventListener("click", () =>
                actions.setSort(filters().sort === kRandomSort ? "" : kRandomSort),
            );
        },
    );
    const sortControls = createEl("div", styles.sortControls, [shuffleButton, sortMenu.el]);

    // ── Keep every pill in step with the filters ─────────────────────────────
    const refreshAll = (): void => {
        for (const menu of menus) {
            menu.refresh();
        }
        sortMenu.refresh();
        refreshKey();
        typeRadios.forEach((radio, value) => {
            radio.checked = filters().category === value;
        });
        shuffleButton.classList.toggle(styles.filterButtonActive, filters().sort === kRandomSort);
        clearAllButton.style.display = hasActiveFilters(filters()) ? "" : "none";
        renderTagRow();
        renderSort();
    };
    signals.filters.listen(refreshAll);
    refreshAll();

    return { filterRow, tagRow, sortControls };
};
